/**
 * Video Element Player
 */

import * as React from 'react';
import { cn, Card, CardContent } from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../i18n/elementUI';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import { resolveMediaUrlForViewer, shouldResolveMediaUrlForViewer } from '@ai-content-hub/content-core';
import { Loader2 } from 'lucide-react';
import type { VideoElementData } from './schema';

export interface VideoPlayerProps extends ElementPlayerProps<VideoElementData> {}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    const rawSrc = (data?.src ?? '').trim();
    const rawPoster = (data?.poster ?? '').trim();

    const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(() =>
        rawSrc && !shouldResolveMediaUrlForViewer(rawSrc) ? rawSrc : null,
    );
    const [resolving, setResolving] = React.useState(() =>
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)),
    );

    const [resolvedPoster, setResolvedPoster] = React.useState<string | null>(() =>
        rawPoster && !shouldResolveMediaUrlForViewer(rawPoster) ? rawPoster : null,
    );
    const [resolvingPoster, setResolvingPoster] = React.useState(() =>
        Boolean(rawPoster && shouldResolveMediaUrlForViewer(rawPoster)),
    );

    React.useEffect(() => {
        const s = (data?.src ?? '').trim();
        if (!s || typeof window === 'undefined') {
            setResolvedSrc(null);
            setResolving(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(s)) {
            setResolvedSrc(s);
            setResolving(false);
            return;
        }
        let cancelled = false;
        setResolving(true);
        setResolvedSrc(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(s);
                if (!cancelled) {
                    setResolvedSrc(uri || s);
                }
            } catch {
                if (!cancelled) {
                    setResolvedSrc(s);
                }
            } finally {
                if (!cancelled) {
                    setResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data?.src]);

    React.useEffect(() => {
        const p = (data?.poster ?? '').trim();
        if (!p || typeof window === 'undefined') {
            setResolvedPoster(null);
            setResolvingPoster(false);
            return;
        }
        if (!shouldResolveMediaUrlForViewer(p)) {
            setResolvedPoster(p);
            setResolvingPoster(false);
            return;
        }
        let cancelled = false;
        setResolvingPoster(true);
        setResolvedPoster(null);
        void (async () => {
            try {
                const uri = await resolveMediaUrlForViewer(p);
                if (!cancelled) {
                    setResolvedPoster(uri || p);
                }
            } catch {
                if (!cancelled) {
                    setResolvedPoster(p);
                }
            } finally {
                if (!cancelled) {
                    setResolvingPoster(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data?.poster]);

    // Validate data exists
    if (!data) {
        return (
            <Card className={cn('video-element-placeholder', className)}>
                <CardContent className="py-8 text-center text-muted-foreground">
                    No video data
                </CardContent>
            </Card>
        );
    }

    const {
        src,
        title,
        poster,
        autoplay = false,
        loop = false,
        muted = false,
        showControls = true,
        aspectRatio = '16:9',
    } = data;

    // Aspect ratio classes
    const aspectRatioClasses: Record<string, string> = {
        '16:9': 'aspect-video',
        '4:3': 'aspect-[4/3]',
        '1:1': 'aspect-square',
        '9:16': 'aspect-[9/16]',
    };

    if (!src) {
        return (
            <Card className={cn('video-element-placeholder', className)}>
                <CardContent className="py-8 text-center text-muted-foreground">
                    No video selected
                </CardContent>
            </Card>
        );
    }

    const displaySrc = resolvedSrc ?? '';
    const displayPoster = poster ? resolvedPoster ?? '' : undefined;
    const showProtectedLoading =
        Boolean(rawSrc && shouldResolveMediaUrlForViewer(rawSrc)) && (resolving || !resolvedSrc);
    const posterBlocking =
        Boolean(rawPoster && shouldResolveMediaUrlForViewer(rawPoster)) &&
        (resolvingPoster || !resolvedPoster);

    return (
        <div className={cn('video-element', className)}>
            {title && <h4 className="text-sm font-medium mb-2">{title}</h4>}
            <div
                className={cn(
                    'relative rounded-lg overflow-hidden bg-black',
                    aspectRatioClasses[aspectRatio] || aspectRatioClasses['16:9']
                )}
            >
                {showProtectedLoading || posterBlocking ? (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground bg-muted/20">
                        <Loader2 className="h-6 w-6 animate-spin shrink-0" />
                        <span>{et.loadingVideo}</span>
                    </div>
                ) : (
                    <video
                        src={displaySrc}
                        poster={displayPoster}
                        controls={showControls}
                        autoPlay={autoplay}
                        loop={loop}
                        muted={muted || autoplay} // Autoplay requires muted
                        className="absolute inset-0 w-full h-full object-contain"
                        playsInline
                    >
                        Your browser does not support the video element.
                    </video>
                )}
            </div>
        </div>
    );
};

export default VideoPlayer;
